import type { ReactNode } from "react"
import { ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { Shimmer } from "../Shimmer/Shimmer"

const DEFAULT_KPI_LABELS = ["RETURN", "NET PROFIT", "WIN RATE", "MAX DD"]

const DEFAULT_SUMMARY_TABS = ["Overview", "Trades", "Risk"]

const DEFAULT_OVERVIEW_METRICS = [
  "Profit Factor",
  "Gross Profit",
  "Sharpe Ratio",
  "Gross Loss",
  "Max Drawdown",
  "Best Trade",
  "Expectancy",
  "Avg Win",
  "Win Rate",
  "Avg Loss",
  "Win / Loss",
  "Avg Hold",
]

const DEFAULT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

export interface BacktestResultsSkeletonProps {
  kpiLabels?: string[]
  chartTitle?: ReactNode
  chartSubtitle?: ReactNode
  realisedLabel?: string
  equityLabel?: string
  summaryTabs?: string[]
  overviewMetricLabels?: string[]
  monthLabels?: string[]
  monthlyRowCount?: number
  yearColumnLabel?: string
  totalColumnLabel?: string
  monthlyTitle?: ReactNode
  promptMessage?: ReactNode
  className?: string
}

export function BacktestResultsSkeleton({
  kpiLabels = DEFAULT_KPI_LABELS,
  chartTitle = "Gain (%)",
  chartSubtitle = "Realised gain vs floating equity over backtest period",
  realisedLabel = "Realised",
  equityLabel = "Equity",
  summaryTabs = DEFAULT_SUMMARY_TABS,
  overviewMetricLabels = DEFAULT_OVERVIEW_METRICS,
  monthLabels = DEFAULT_MONTHS,
  monthlyRowCount = 3,
  yearColumnLabel = "Year",
  totalColumnLabel = "Total",
  monthlyTitle = "Monthly Returns",
  promptMessage,
  className,
}: BacktestResultsSkeletonProps) {
  const defaultPrompt: ReactNode = (
    <>
      Describe your strategy and
      <br />
      AI will build and backtest it here.
      <ArrowRight
        size={14}
        strokeWidth={1.5}
        className="inline-block align-middle ml-1"
      />
    </>
  )

  return (
    <div className={cn("flex flex-col h-full overflow-y-auto bg-card", className)}>
      <div className="flex border-b border-border shrink-0">
        {kpiLabels.map((label, i) => (
          <div
            key={label}
            className={cn(
              "flex-1 min-w-0 flex flex-col items-start gap-1 px-2 sm:px-4 py-3",
              i < kpiLabels.length - 1 && "border-r border-border",
            )}
          >
            <span className="text-caption font-semibold uppercase tracking-normal sm:tracking-wider text-muted-foreground">
              {label}
            </span>
            <Shimmer className="h-5 w-16 sm:w-24" />
          </div>
        ))}
      </div>

      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex flex-col gap-1">
            <div className="text-section text-foreground">{chartTitle}</div>
            <div className="text-caption text-muted-foreground">{chartSubtitle}</div>
          </div>
          <div className="flex items-center gap-3 text-caption text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-0.5 rounded-full bg-[#26a69a]" />
              {realisedLabel}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 border-t border-dashed border-muted-foreground" />
              {equityLabel}
            </span>
          </div>
        </div>
        <div className="relative h-[240px] w-full rounded shimmer-bar flex items-center justify-center overflow-hidden">
          <svg
            aria-hidden="true"
            viewBox="0 0 800 240"
            preserveAspectRatio="none"
            className="absolute inset-0 w-full h-full pointer-events-none"
          >
            <g
              stroke="var(--border)"
              strokeOpacity="0.5"
              strokeDasharray="3 3"
              strokeWidth="1"
            >
              <line x1="44" y1="30" x2="795" y2="30" />
              <line x1="44" y1="75" x2="795" y2="75" />
              <line x1="44" y1="120" x2="795" y2="120" />
              <line x1="44" y1="165" x2="795" y2="165" />
            </g>
            <g
              stroke="var(--muted-foreground)"
              strokeOpacity="0.18"
              strokeWidth="1"
            >
              <line x1="44" y1="10" x2="44" y2="210" />
              <line x1="44" y1="210" x2="795" y2="210" />
            </g>
            {[30, 75, 120, 165, 210].map((y) => (
              <g key={`y-${y}`}>
                <line
                  x1="40"
                  y1={y}
                  x2="44"
                  y2={y}
                  stroke="var(--muted-foreground)"
                  strokeOpacity="0.18"
                  strokeWidth="1"
                />
                <rect
                  x="14"
                  y={y - 3}
                  width="22"
                  height="6"
                  rx="1"
                  fill="var(--muted-foreground)"
                  fillOpacity="0.10"
                />
              </g>
            ))}
            {[80, 230, 420, 610, 780].map((x) => (
              <g key={`x-${x}`}>
                <line
                  x1={x}
                  y1="210"
                  x2={x}
                  y2="214"
                  stroke="var(--muted-foreground)"
                  strokeOpacity="0.18"
                  strokeWidth="1"
                />
                <rect
                  x={x - 14}
                  y="220"
                  width="28"
                  height="6"
                  rx="1"
                  fill="var(--muted-foreground)"
                  fillOpacity="0.10"
                />
              </g>
            ))}
            <polyline
              points="45,192 60,189 75,193 90,185 105,180 120,183 135,178 150,174 165,171 180,176 195,168 210,160 225,164 240,156 255,150 270,153 285,148 300,142 315,137 330,141 345,132 360,125 375,128 390,120 405,113 420,118 435,128 450,140 465,145 480,152 495,147 510,140 525,132 540,125 555,120 570,112 585,115 600,105 615,98 630,92 645,95 660,85 675,78 690,72 705,66 720,70 735,58 750,50 765,42 780,38 795,30"
              fill="none"
              stroke="var(--muted-foreground)"
              strokeOpacity="0.22"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="relative z-10 hidden min-[1100px]:block text-caption text-muted-foreground text-center leading-relaxed">
            {promptMessage ?? defaultPrompt}
          </p>
        </div>
      </div>

      <div className="border-b border-border">
        <div className="flex items-center gap-4 px-4 border-b border-border shrink-0 h-11">
          {summaryTabs.map((t, i) => (
            <span
              key={t}
              className={cn(
                "text-caption pb-1 border-b-[1.5px]",
                i === 0
                  ? "font-medium text-foreground border-foreground"
                  : "font-normal text-muted-foreground border-transparent",
              )}
            >
              {t}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 px-4 py-4">
          {overviewMetricLabels.map((label) => (
            <div
              key={label}
              className={cn(
                "flex items-center justify-between py-2 border-b border-border",
                "[&:last-child]:border-b-0",
                "md:[&:nth-last-child(-n+2)]:border-b-0",
              )}
            >
              <span className="text-caption text-muted-foreground uppercase tracking-wider">
                {label}
              </span>
              <Shimmer className="h-3 w-16" />
            </div>
          ))}
        </div>
      </div>

      <div className="border-b border-border px-4 py-4">
        <div className="flex flex-col gap-1 mb-3">
          <div className="text-section text-foreground">{monthlyTitle}</div>
          <Shimmer className="h-2.5 w-40" />
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-caption">
            <thead>
              <tr className="border-b border-border">
                <th className="text-caption font-medium text-muted-foreground uppercase tracking-wider px-2 py-1.5 text-left w-12">
                  {yearColumnLabel}
                </th>
                {monthLabels.map((m) => (
                  <th
                    key={m}
                    className="text-caption font-medium text-muted-foreground uppercase tracking-wider px-2 py-1.5 text-right"
                  >
                    {m}
                  </th>
                ))}
                <th className="text-caption font-medium text-muted-foreground uppercase tracking-wider px-2 py-1.5 text-right">
                  {totalColumnLabel}
                </th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: monthlyRowCount }).map((_, row) => (
                <tr key={row} className="border-b border-border last:border-b-0">
                  <td className="px-2 py-1.5">
                    <Shimmer className="h-3 w-10" />
                  </td>
                  {Array.from({ length: monthLabels.length }).map((_, col) => (
                    <td key={col} className="px-2 py-1.5">
                      <Shimmer className="h-3 w-10 ml-auto" />
                    </td>
                  ))}
                  <td className="px-2 py-1.5">
                    <Shimmer className="h-3 w-12 ml-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden">
          <table className="w-full text-caption">
            <thead>
              <tr className="border-b border-border">
                <th className="text-caption font-medium text-muted-foreground uppercase tracking-wider px-2 py-1.5 text-left w-16">
                  {yearColumnLabel}
                </th>
                <th className="text-caption font-medium text-muted-foreground uppercase tracking-wider px-2 py-1.5 text-right">
                  {totalColumnLabel}
                </th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: monthlyRowCount }).map((_, row) => (
                <tr key={row} className="border-b border-border last:border-b-0">
                  <td className="px-2 py-3">
                    <Shimmer className="h-3 w-12" />
                  </td>
                  <td className="px-2 py-3">
                    <Shimmer className="h-3 w-14 ml-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
